// NEW_FILE_JUSTIFIED: Upload step for bulk import page — no existing component handles multi-format bulk upload
import { useRef, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, FileText, X, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IBulkImportUploadStepProps } from '@/@types/bulkImport';

const ACCEPTED_TYPES = '.pdf,.xlsx,.xls,.csv,.doc,.docx';
const ACCEPTED_LABEL = 'PDF, Excel (.xlsx/.xls), CSV, Word (.docx)';

export function BulkImportUploadStep({ onFilesSelected, isUploading }: IBulkImportUploadStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    setPendingFiles(prev => [...prev, ...Array.from(files)]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const removeFile = useCallback((index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleStart = useCallback(() => {
    if (pendingFiles.length === 0) return;
    onFilesSelected(pendingFiles);
  }, [pendingFiles, onFilesSelected]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold text-foreground">Upload Renewal Files</h2>
        <p className="text-sm text-muted-foreground">
          Upload one or more renewal list files from your insurance provider. AI will extract all
          policies automatically.
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={ACCEPTED_TYPES}
        multiple
        onChange={e => {
          handleFileSelect(e.target.files);
          e.target.value = '';
        }}
      />

      <div
        role="button"
        tabIndex={0}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border',
          'bg-card py-12 px-6 text-center cursor-pointer hover:bg-muted/50 transition-colors'
        )}
      >
        <Upload className="w-10 h-10 text-muted-foreground" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-foreground">Click or drag files to upload</p>
          <p className="text-xs text-muted-foreground mt-1">{ACCEPTED_LABEL}</p>
        </div>
      </div>

      {pendingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-foreground">
            Selected files ({pendingFiles.length})
          </p>
          {pendingFiles.map((file, index) => (
            <div
              key={`${file.name}-${file.size}-${index}`}
              className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/50"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
                <span className="text-sm text-foreground truncate">{file.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 shrink-0"
                onClick={e => {
                  e.stopPropagation();
                  removeFile(index);
                }}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleStart} disabled={pendingFiles.length === 0 || isUploading}>
          <Play className="w-4 h-4 mr-2" aria-hidden="true" />
          Start Extraction
        </Button>
      </div>
    </div>
  );
}
